import { Injectable,inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, tap, map } from 'rxjs';
//import { environment } from '../../../../../../apps/rslweb-portal/src/environments/environment';
import * as CryptoJS from 'crypto-js';
import { APP_CONFIG } from '@rslweb/app-config';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  readonly appConfig = inject(APP_CONFIG);
  private apiUrl = `${this.appConfig.apiUrl}users/permissions`;
  private permissionsLoaded = false;
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  private readonly secretKey = this.appConfig.secretKey;

  permissions$ = this.permissionsSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadCachedPermissions();
  }

  private loadCachedPermissions(): void {
    const encryptedPermissions = sessionStorage.getItem(
      'encrypted_permissions'
    );
    if (encryptedPermissions) {
      try {
        const decryptedPermissions =
          this.decryptPermissions(encryptedPermissions);
        this.permissionsSubject.next(decryptedPermissions);
        this.permissionsLoaded = true;
      } catch (error) {
        console.error('Error loading cached permissions:', error);
        this.clearPermissions();
      }
    }
  }

  private encryptPermissions(permissions: string[]): string {
    try {
      const permissionsString = JSON.stringify(permissions);
      const encrypted = CryptoJS.AES.encrypt(
        permissionsString,
        this.secretKey
      ).toString();
      return encrypted;
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt permissions');
    }
  }

  private decryptPermissions(encryptedPermissions: string): string[] {
    try {
      const decrypted = CryptoJS.AES.decrypt(
        encryptedPermissions,
        this.secretKey
      );
      const permissionsString = decrypted.toString(CryptoJS.enc.Utf8);
      if (!permissionsString) {
        throw new Error('Decryption resulted in empty string');
      }
      return JSON.parse(permissionsString);
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt permissions');
    }
  }

  loadUserPermissions(): Observable<string[]> {
    if (this.permissionsLoaded) {
      return of(this.permissionsSubject.value);
    }

    const token = sessionStorage.getItem('token');
    if (!token) {
      return throwError(() => new Error('No authentication token found'));
    }

    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.get<any>(this.apiUrl, { headers }).pipe(
      map((response) => {
        const userTableId = response.userTableId;
        sessionStorage.setItem('user_table_id', userTableId);

        const empNum = response.employeeNumber;
        sessionStorage.setItem('emp_num', empNum);

        const empId = response.employeeId;
        sessionStorage.setItem('emp_id', empId);

        const empDept = response.department;
        sessionStorage.setItem('emp_dept', empDept);

        const jobrole = response.roleName;
        sessionStorage.setItem('jobRole', jobrole);

        console.log('emp_num', empNum);

        const permissionNames = response.data.map(
          (p: { permission_name: string }) => p.permission_name
        );
        return permissionNames;
      }),
      tap((permissions) => {
        try {
          // Encrypt permissions before storing
          const encryptedPermissions = this.encryptPermissions(permissions);
          sessionStorage.setItem('encrypted_permissions', encryptedPermissions);

          this.permissionsSubject.next(permissions);
          this.permissionsLoaded = true;
        } catch (error) {
          console.error('Error storing permissions:', error);
          throw error;
        }
      }),
      catchError((error) => {
        console.error('Error loading permissions:', error);
        return throwError(() => error);
      })
    );
  }

  clearPermissions(): void {
    this.permissionsLoaded = false;
    this.permissionsSubject.next([]);
    sessionStorage.removeItem('encrypted_permissions');
  }

  hasPermission(requiredPermission: string | string[]): boolean {
    try {
      const currentPermissions = this.permissionsSubject.value;
      if (Array.isArray(requiredPermission)) {
        return requiredPermission.some((permission) =>
          currentPermissions.includes(permission)
        );
      }

      return currentPermissions.includes(requiredPermission);
    } catch (error) {
      console.error('Error checking permissions:', error);
      return false;
    }
  }

  getCurrentPermissions(): string[] {
    return this.permissionsSubject.value;
  }

  validateStoredPermissions(): boolean {
    try {
      const encryptedPermissions = sessionStorage.getItem(
        'encrypted_permissions'
      );
      if (!encryptedPermissions) return false;

      const decryptedPermissions =
        this.decryptPermissions(encryptedPermissions);
      return Array.isArray(decryptedPermissions);
    } catch (error) {
      console.error('Permission validation failed:', error);
      return false;
    }
  }
}
